/*
  RS cart refresh: the cart drawer, the header cart count and the sticky navigation count show the
  real cart again when a page comes back on screen.
  - Back / Forward: the browser can show the page from its memory (back/forward cache) exactly as it
    was left, or from its HTTP cache, with the cart of that moment.
  - A tab shown again: the cart may have changed in another tab or window meanwhile.
  In both cases /cart.js is read and compared with the cart the page shows (lines and quantities,
  total). Only when it differs: the drawer content and the cart icon are rendered again with the
  same sections Dawn uses (cart-drawer, cart-icon-bubble), and the cart page is reloaded (its
  lines, summary and empty state are all built on the server). The drawer is not opened.
  Loaded by snippets/rs-cart-refresh.liquid, which gives the cart shown when the page was built.
*/
(() => {
  if (window.rsCartRefresh) return;
  window.rsCartRefresh = true;
  if (window.Shopify && window.Shopify.designMode) return;

  const script = document.currentScript;
  const isCartPage = script && script.dataset.cartPage === 'true';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const MIN_HIDDEN_MS = 2000;

  // Same format as the Liquid in snippets/rs-cart-refresh.liquid.
  const signature = (cart) => `${cart.items.map((item) => `${item.key}:${item.quantity}`).join(',')}|${cart.total_price}`;

  let shown = script ? script.dataset.cart : null;
  let hiddenAt = 0;
  let running = null;

  const readCart = () =>
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  const render = async (cart) => {
    const drawer = document.querySelector('cart-drawer');
    const ids = ['cart-icon-bubble'];
    if (drawer) ids.push('cart-drawer');
    const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(response.status);
    const sections = await response.json();

    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleSource = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
    if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;

    if (drawer && sections['cart-drawer']) {
      const inner = drawer.querySelector('.drawer__inner');
      const source = parse(sections['cart-drawer']).querySelector('.drawer__inner');
      if (inner && source) inner.innerHTML = source.innerHTML;
      drawer.classList.toggle('is-empty', cart.item_count === 0);
    }

    document.querySelectorAll('.rs-navigation__count').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = !cart.item_count;
    });
  };

  const check = () => {
    if (running) return running;
    running = readCart()
      .then(async (cart) => {
        const current = signature(cart);
        if (current === shown) return;
        if (isCartPage) {
          window.location.reload();
          return;
        }
        await render(cart);
        shown = current;
      })
      .catch((error) => console.error('RS cart refresh: could not check the cart', error))
      .finally(() => (running = null));
    return running;
  };

  // The theme's own cart changes update the page themselves: take the new cart as the one shown.
  // On the cart page the lines and the drawer are two Dawn <cart-items>, and each ignores the other's
  // changes (source "cart-items"): a line removed on the page stayed in the drawer (its "remove" then
  // failed with "Il parametro line non è valido"), a change in the drawer did not reach the page.
  // The part that was not used is brought up to date: the closed drawer is drawn again in full
  // (heading, free shipping bar and lines; Dawn's own refresh redraws only the lines and the
  // footer), the page lines and summary after a change made in the open drawer.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      readCart()
        .then(async (cart) => {
          shown = signature(cart);
          if (!isCartPage) return;
          const drawer = document.querySelector('cart-drawer');
          if (drawer && !drawer.classList.contains('active')) {
            await render(cart);
          } else if (event && event.source === 'cart-items') {
            document.getElementById('main-cart-items')?.closest('cart-items')?.onCartUpdate?.();
          }
        })
        .catch(() => {});
    });
  }

  // From the back/forward cache: the page is shown again as it was left.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) check();
  });

  // Back / Forward without that cache: the browser can still take the page from its HTTP cache.
  const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (navigation && navigation.type === 'back_forward') check();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
    } else if (hiddenAt && Date.now() - hiddenAt >= MIN_HIDDEN_MS) {
      check();
    }
  });
})();
